import {useEffect, useRef, useState, forwardRef} from "react";
import { TreeItem } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {ArrowUpward, ArrowDownward, Remove, DragIndicator, Edit} from "@mui/icons-material";
import {IconButton, Modal, Box, Typography, Autocomplete} from "@mui/material";
import CustomTextField from "./CustomTextField.tsx";
import {DefaultButton} from "./Buttons.tsx";

interface AutocompleteTextInputProps {
    items: string[];
    setItems: React.Dispatch<React.SetStateAction<string[]>>;
    suggestions: string[];
    headline: string;
    textfieldLabel: string;
}

const AutocompleteTextInput = forwardRef<HTMLInputElement, AutocompleteTextInputProps>(({ items, setItems, suggestions, headline, textfieldLabel}, ref) => {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editText, setEditText] = useState("");
  const [inputText, setInputText] = useState<string>("");
  const [inputTextError, setInputTextError] = useState<boolean>(false);
  const [inputTextTouched, setInputTextTouched] = useState(false);
  const inputRef = useRef(null);

    // Sync the forwarded ref with internal ref
    useEffect(() => {
        if (typeof ref === 'function') {          // i.e. callback ref (ref is a function)
            ref(inputRef.current);
        } else if (ref) {                         // i.e. ref created with useRef()
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
        }
    }, [ref]);


  const moveItem = (index, direction) => {
    const newItems = [...items];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
  };

  const handleDragStart = (index) => setDraggingIndex(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;
    const newItems = [...items];
    const [draggedItem] = newItems.splice(draggingIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggingIndex(index);
  };

  const handleDrop = () => {
    setDraggingIndex(null);
  };

  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditText(items[index]);
  };


  useEffect(() => {setInputTextError(inputTextTouched && !inputText);}, [inputText, inputTextTouched]);

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "80%", gap: "15px", borderStyle: "solid", borderWidth: "1px", borderRadius: "10px", paddingBottom: "15px"}}>
      <h3>{headline}</h3>
      <div style={{ display:'flex', flexDirection: 'row', alignItems: 'start', columnGap: '15px', justifyContent: 'center', width: "100%", minWidth: "100%", marginBottom: "15px" }}>
      <Autocomplete
        freeSolo
        options={suggestions}
        value={inputText}
        onChange={(_e, newValue) => {setInputText(newValue); setTimeout(() => inputRef.current?.setSelectionRange(0, 0), 0);}} // This moves the caret to the beginning of the text
        onInputChange={(_e, newValue) => setInputText(newValue)}
        renderInput={(params) => (
            <CustomTextField
              {...params}
              value={null}
              label={textfieldLabel}
              is_required={true}
              manualError={inputTextError}
              errorText={"Dieses Feld darf nicht leer sein!"}
              sx={{ width: "650px" }}
              onBlur={()=>{setInputTextTouched(true)}}
              inputRef={inputRef}
              onKeyDown={(e) => {
                if (e.key === "Enter" && inputText) {
                  e.preventDefault();
                  setItems([...items, inputText]);
                  setInputText("");
                  setInputTextTouched(false);
                }
              }}
            />
          )}
      />
      <DefaultButton label={"Hinzufügen"}
               onClick={() => {
                 if (inputText) {
                   setItems([...items, inputText]);
                   setInputText("");
                   setInputTextTouched(false);
                 } else {
                   setInputTextTouched(true);
                 }}}
               sx = {{ width: "125px", minWidth: "125px", height: "55px"}} variant={"contained"} color={!inputText ? "red" : ""}/>
    </div>
      <SimpleTreeView>
        {items.map((item, index) => (
          <TreeItem
            key={index}
            itemId={String(index)}
            label={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "transparent",
                  padding: "8px",
                  borderRadius: "4px",
                  justifyContent: "center"
                }}
                onDoubleClick={() => handleEditClick(index)}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={handleDrop}
              >
                <IconButton {...{ "aria-label": "drag" }}>
                  <DragIndicator />
                </IconButton>
                <CustomTextField
                  label={textfieldLabel}
                  value={item}
                  sx={{ width: "650px" }}
                  onClick={(e) => e.stopPropagation()}
                  // When live editing should be possible
                  onChange={(e) => setItems((prev) => prev.map((item, i) => i==index ? e.target.value : item))}
                  onKeyDown={(e) => e.stopPropagation()} /* Necessary to be able to enter spacebar for some reason */
                  // Set this to true if editing should only be possible with a double-click
                  // inputProps={{readOnly: true}}
                  // onDoubleClick={(e) => {
                  //   e.stopPropagation();
                  //   handleEditClick(index);
                  // }}
                />
                <IconButton onClick={() => moveItem(index, -1)} disabled={index === 0}>
                  <ArrowUpward />
                </IconButton>
                <IconButton onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}>
                  <ArrowDownward />
                </IconButton>
                <IconButton onClick={() => handleEditClick(index)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setItems(items.filter((_, i) => i !== index))} color="error">
                  <Remove />
                </IconButton>
              </div>
            }
            sx={{
              "& .MuiTreeItem-content.Mui-selected": {
                backgroundColor: "transparent",
              },
              "& .MuiTreeItem-content.Mui-selected:hover": {
                backgroundColor: "var(--primary-color)",
              },
              "& .MuiTreeItem-content:hover": {
                backgroundColor: "var(--primary-color)"
              }
            }}
          />
        ))}
      </SimpleTreeView>

      {/* Edit Modal */}
      <Modal open={editIndex !== null} onClose={() => setEditIndex(null)}>
        <Box sx={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 600, bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: "8px"}}>
          <Typography variant="h6">Eintrag bearbeiten</Typography>
          <p></p>
          <CustomTextField
            label={textfieldLabel}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            sx={{width: "100%", marginBottom: "16px"}}
            manualError={!editText}
            errorText={"Dieses Feld darf nicht leer sein!"}
          />
          <div style={{display: "flex", flexDirection: "row", gap: "15px", justifyContent: "center"}}>
            <DefaultButton label={"Abbrechen"}
                           onClick={() => setEditIndex(null)}
                           sx={{width: "125px", minWidth: "125px", height: "55px"}} color={"red"} variant={"contained"}/>
            <DefaultButton label={"Speichern"}
                           onClick={() => {
                             if (editText) {
                                setItems((prev) => prev.map((item, i) => i==editIndex ? editText : item))
                                setEditIndex(null);
                             }
                           }}
                           sx={{width: "125px", minWidth: "125px", height: "55px"}}/>
          </div>
        </Box>
      </Modal>
    </div>
);
});

export default AutocompleteTextInput;
